<template>
  <!-- 操作栏 -->
  <el-row justify="end">
    <el-button @click="openForm('create')">
      <Icon class="mr-5px" icon="icon-park:income-one" />
      创建回款
    </el-button>
  </el-row>

  <!-- 列表 -->
  <ContentWrap class="mt-10px">
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <!-- ★ 列 / 列宽 / 插槽与「回款管理」(views/crm/receivable/index.vue) 的「追加段」保持一致 (2026-09-20)
           ⚠️ 2026-09-21：回款管理列表已按原型（原型图/客户/财务/*-回款-列表页.png）改制为
              「原型列段 + 追加段」；本组件**暂未同步原型列段**（订单编号 / 合同类型 / 收付款 /
              付款方式 / 付款分期 / 说明 等）。理由：本组件只出现在客户详情 / 合同详情页签，
              定位是「该客户（合同）的回款流水」，刻意保持精简。如需与原型完全一致，告知后再同步。
           有意差异：
           ① 不显示「客户名称」列 —— 本组件只用于客户详情 / 合同详情，列表已按单个客户过滤。
           ② 「操作」列保留本组件原有实现（仅 编辑 / 删除），不把管理页的
              「提交审核 / 查看审批」搬进来（那是管理页的职责）。
           ③ 2026-09-22：补「是否收款,付款」列（回款自身字段，非合同属性）—— 它是回款的核心状态，
              与本组件「回款流水」的定位强相关，故不受上面「暂不同步原型列段」的限制。 -->
      <el-table-column align="center" fixed="left" label="回款编号" prop="no" width="180">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.no }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="合同编号" prop="contractNo" width="180">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openContractDetail(scope.row.contractId)"
          >
            {{ scope.row.contract.no }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter2"
        align="center"
        label="回款日期"
        prop="returnTime"
        width="150px"
      />
      <el-table-column
        align="center"
        label="回款金额(元)"
        prop="price"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <el-table-column align="center" label="回款方式" prop="returnType" width="130px">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_RECEIVABLE_RETURN_TYPE" :value="scope.row.returnType" />
        </template>
      </el-table-column>
      <!-- ★ 2026-09-22 新增：是否收款,付款（回款自身字段，不是合同属性）。
           它是回款的核心状态，与本组件「回款流水」的定位强相关，故不受上面
           「暂未同步原型列段」的限制。字典 crm_receivable_received_flag（1 是 / 0 否）。 -->
      <el-table-column align="center" label="是否收款,付款" prop="receivedFlag" width="120">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_RECEIVABLE_RECEIVED_FLAG"
            :value="scope.row.receivedFlag"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="备注" prop="remark" width="200" />
      <el-table-column
        align="center"
        label="合同金额（元）"
        prop="contract.totalPrice"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <el-table-column align="center" label="负责人" prop="ownerUserName" width="120" />
      <el-table-column align="center" label="所属部门" prop="ownerUserDeptName" width="100px" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="更新时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180px"
      />
      <el-table-column align="center" label="创建人" prop="creatorName" width="120" />
      <!-- ★ 「回款状态」列（即审批状态 auditStatus）按需求隐藏（2026-09-21）：审批入口已下线，
           该列恒为「未提交」。恢复：取消注释即可。 -->
      <!-- <el-table-column align="center" label="回款状态" prop="auditStatus" width="120">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_AUDIT_STATUS" :value="scope.row.auditStatus" />
        </template>
      </el-table-column> -->
      <el-table-column align="center" fixed="right" label="操作" width="130px">
        <template #default="scope">
          <el-button
            v-hasPermi="['crm:receivable:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['crm:receivable:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加 -->
  <ReceivableForm ref="formRef" @success="getList" />
</template>
<script lang="ts" setup>
import * as ReceivablePlanApi from '@/api/crm/receivable/plan'
import * as ReceivableApi from '@/api/crm/receivable'
import ReceivableForm from './../ReceivableForm.vue'
import { dateFormatter, dateFormatter2 } from '@/utils/formatTime'
import { DICT_TYPE } from '@/utils/dict'
import { erpPriceTableColumnFormatter } from '@/utils'

defineOptions({ name: 'CrmReceivableList' })
const props = defineProps<{
  customerId?: number // 客户编号
  contractId?: number // 合同编号
}>()

/** 路由跳转（对齐回款管理列表：回款编号 / 合同编号可点击进入详情） */
const { push } = useRouter()
/** 打开回款详情 */
const openDetail = (id: number) => {
  push({ name: 'CrmReceivableDetail', params: { id } })
}
/** 打开合同详情 */
const openContractDetail = (id: number) => {
  push({ name: 'CrmContractDetail', params: { id } })
}

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化
const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  customerId: undefined as unknown, // 允许 undefined + number
  contractId: undefined as unknown // 允许 undefined + number
})

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    if (props.customerId && !props.contractId) {
      queryParams.customerId = props.customerId
    } else if (props.customerId && props.contractId) {
      // 如果是合同的话客户编号也需要带上因为权限基于客户
      queryParams.customerId = props.customerId
      queryParams.contractId = props.contractId
    }
    const data = await ReceivableApi.getReceivablePageByCustomer(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  // 置空参数
  queryParams.customerId = undefined
  queryParams.contractId = undefined
  getList()
}

/** 添加/修改操作：把当前客户/合同作为「预填上下文」放在第 4 个参数传给表单。
 *  ⚠️ 不能塞在第 3 个参数上 —— 那是「回款计划」，会让表单在「修改」时也走该分支，
 *     把刚从接口查出来的合同、回款期数覆盖掉。 */
const formRef = ref()
const openForm = (type: string, id?: number) => {
  formRef.value.open(type, id, undefined, {
    customerId: props.customerId,
    contractId: props.contractId
  })
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await ReceivableApi.deleteReceivable(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 从回款计划创建回款 */
const createReceivable = (planData: any) => {
  const data = planData as unknown as ReceivablePlanApi.ReceivablePlanVO
  formRef.value.open('create', undefined, data)
}
defineExpose({ createReceivable })

/** 监听打开的 customerId + contractId，从而加载最新的列表 */
watch(
  () => [props.customerId, props.contractId],
  (newVal) => {
    // 保证至少客户编号有值
    if (!newVal[0]) {
      return
    }
    handleQuery()
  },
  { immediate: true, deep: true }
)
</script>
